// Creator dashboard, assets: your assets and their versions (review status, the moderator's note, yanking) and how
// to publish with `thrdm publish`. Moderators get the review queue: automated checks, a preview of the baked model
// and the manifest diff from the previous version, then approve or reject with a note.

import { ApiError } from "../api.ts";
import { clear, errorBox, h, link, section, toast } from "../dom.ts";
import { startAssetPreview } from "../preview-frame.ts";
import { store, type StoreBackend } from "../store-client.ts";
import {
  assetPath,
  canYank,
  checkSummary,
  manifestDiff,
  renderJobText,
  STATUS_LABEL,
  type AssetDetail,
  type AssetVersion,
  type Check,
  type DiffLine,
  type RenderJob,
  type RenderQueue,
  type ReviewItem,
} from "../store.ts";
import { offerText, sampleNote, statsList } from "./store.ts";

const day = (unix: number) => (unix ? new Date(unix * 1000).toISOString().slice(0, 10) : "—");

/**
 * Does the store let this user review? (the queue answers 403 for everyone else). Asked once per user per page load.
 * The answer is the queue itself, so `/dashboard/review` uses it for its first draw instead of fetching it again.
 */
let moderator: { key: string; queue: Promise<ReviewItem[]>; ok: Promise<boolean>; fresh: boolean; at: number } | null = null;
export function isAssetModerator(userId: string): Promise<boolean> {
  if (moderator?.key !== userId) {
    const queue = store().then((b) => b.queue());
    moderator = { key: userId, queue, ok: queue.then(() => true, () => false), fresh: true, at: Date.now() };
  }
  return moderator.ok;
}

/** The nav probe's queue, once and only while it's recent (later draws, after approve or reject, fetch again). */
export function takeProbedQueue(now = Date.now()): Promise<ReviewItem[]> | null {
  if (!moderator?.fresh || now - moderator.at > 30_000) return null;
  moderator.fresh = false;
  return moderator.queue;
}

export function publishHelp(): HTMLElement {
  return h(
    "section",
    { class: "panel" },
    h("h2", null, "Publish with the Threedom CLI"),
    h("p", null, "You upload from your computer with the Threedom CLI, not from the browser. It runs the store's checks first, so most problems show up before anything is sent."),
    h("pre", { class: "code" }, h("code", null, "thrdm publish ./my-asset            # dry run: checks it and shows what would be uploaded\nthrdm publish ./my-asset --confirm  # uploads it for review")),
    h(
      "ol",
      { class: "steps" },
      h("li", null, "Put the package in a folder: ", h("code", null, "asset.json"), ", ", h("code", null, "LICENSE"), ", your models in ", h("code", null, "data/"), " and, for settings or behaviour, code in ", h("code", null, "dist/"), "."),
      h("li", null, "Raise ", h("code", null, "version"), " in ", h("code", null, "asset.json"), ". Published versions never change, so each upload needs a new number."),
      h("li", null, "Run ", h("code", null, "thrdm publish"), " without ", h("code", null, "--confirm"), " first. It checks the manifest, file hashes, code and a test render, and shows what it would upload. Nothing is sent until you run it again with ", h("code", null, "--confirm"), "."),
      h("li", null, "The version waits in review. A moderator looks at the checks and the preview, then lists it or tells you what to fix. You'll see the result here."),
    ),
    h("p", { class: "dim small" }, "To take a listed version off the store: ", h("code", null, "thrdm yank <creator>/<name>@<version> --reason \"…\""), ", or the Yank button above."),
    h("p", { class: "dim small" }, "Agents can publish too, with the MCP ", h("code", null, "publish"), " tool. It doesn't upload until you confirm. You can only publish under a creator page you've claimed."),
  );
}

function checksList(checks: Check[]): HTMLElement {
  const mark = { pass: "✓", fail: "✕", warn: "!", skip: "–" } as const;
  return h(
    "ul",
    { class: "checks" },
    checks.map((c) => h("li", { class: `check-${c.result}` }, h("span", { class: "mark", "aria-label": c.result }, mark[c.result]), h("strong", null, c.name), c.detail ? h("span", { class: "dim" }, ` ${c.detail}`) : null)),
  );
}

/** One badge per version: failures first, then the package's warnings. A render that hasn't run (or that a
 * moderator skipped) is the review's business, shown in the queue, not a problem with the creator's version. */
export function checksBadge(checks: Check[] | undefined): HTMLElement | null {
  if (!checks?.length) return null;
  const s = checkSummary(checks.filter((c) => !(c.name === "render" && c.result !== "fail")));
  const title = checks.map((c) => `${c.name}: ${c.result}${c.detail ? ` (${c.detail})` : ""}`).join("\n");
  if (s.fail) return h("span", { class: "badge bad", title }, `${s.fail} failed`);
  if (s.warn) return h("span", { class: "badge warn", title }, `${s.warn} to look at`);
  return h("span", { class: "badge ok", title }, "Passed");
}

// ---- creators: my assets ----------------------------------------------------------------------------------------

function yankForm(b: StoreBackend, a: AssetDetail, v: AssetVersion, done: () => void): HTMLElement {
  const reason = h("input", { type: "text", maxlength: 200, placeholder: "Why? (shown on the asset page)", "aria-label": `Why yank ${a.id}@${v.version}` });
  const form: HTMLFormElement = h(
    "form",
    {
      class: "yank-form",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const btn = form.querySelector("button[type=submit]") as HTMLButtonElement;
        btn.disabled = true;
        try {
          await b.yank(a.id, v.version, reason.value.trim());
          toast(`${a.id}@${v.version} yanked`);
          done();
        } catch (err) {
          toast(err instanceof Error ? err.message : String(err));
          btn.disabled = false;
        }
      },
    },
    reason,
    h("button", { type: "submit", class: "btn small danger" }, "Yank"),
  );
  return form;
}

/** Where a version in review is in the render reviewer's queue (nothing once its render has reported). */
export function renderJobLine(j: RenderJob | null | undefined, status: AssetVersion["status"]): HTMLElement | null {
  if (!j || status !== "pending" || j.state === "done") return null;
  const t = renderJobText(j);
  return h("div", { class: `render-job small ${t.cls === "dim" ? "dim" : t.cls}` }, t.text);
}

function versionRow(b: StoreBackend, a: AssetDetail, v: AssetVersion, redraw: () => void): HTMLElement[] {
  const st = STATUS_LABEL[v.status];
  const detail =
    v.status === "rejected" && v.note
      ? h("div", { class: "mod-note" }, h("strong", null, "Moderator: "), v.note)
      : v.status === "yanked" && v.yankReason
        ? h("div", { class: "dim small" }, v.yankReason)
        : renderJobLine(v.renderJob, v.status);
  const yank = canYank(v)
    ? h(
        "details",
        { class: "yank" },
        h("summary", { class: "btn small ghost" }, "Yank"),
        h("p", { class: "dim small" }, v.status === "listed" ? "It comes off the store. Games that pinned it keep working, with a warning on install." : "It leaves the review queue."),
        yankForm(b, a, v, redraw),
      )
    : null;
  return [
    h(
      "tr",
      null,
      h("td", null, h("code", null, v.version)),
      h("td", null, day(v.publishedAt)),
      h("td", null, h("span", { class: `badge ${st.cls}` }, st.text), detail),
      h("td", null, checksBadge(v.checks)),
      h("td", { class: "actions-cell" }, yank),
    ),
  ];
}

export function myAssetsView(): HTMLElement {
  return h(
    "div",
    null,
    h("h1", null, "Your assets"),
    section(null, async (body) => {
      const b = await store();
      const draw = async () => {
        let list: AssetDetail[];
        try {
          list = await b.mine();
        } catch (e) {
          if (e instanceof ApiError && e.status === 403) {
            clear(body, h("p", { class: "note" }, "Assets are published under a creator page. ", link("/dashboard/claim", null, "Claim yours"), " first."), publishHelp());
            return;
          }
          throw e;
        }
        clear(
          body,
          sampleNote(b),
          list.length === 0 ? h("p", { class: "dim" }, "You haven't published anything yet.") : null,
          list.map((a) => {
            const listed = a.versions.some((v) => v.status === "listed");
            return h(
              "section",
              { class: "panel asset-dash" },
              h(
                "div",
                { class: "asset-dash-head" },
                a.previewUrl ? h("img", { src: a.previewUrl, alt: "", width: 64, height: 64, loading: "lazy" }) : null,
                h(
                  "div",
                  null,
                  h("h2", null, listed ? link(assetPath(a.id), null, a.title) : a.title),
                  h("p", { class: "dim small" }, h("code", null, a.id), ` · ${offerText(a.price)}`, a.downloads !== undefined ? ` · ${a.downloads} installs` : null),
                ),
              ),
              h(
                "div",
                { class: "table-wrap" },
                h(
                  "table",
                  { class: "versions" },
                  h("thead", null, h("tr", null, h("th", null, "Version"), h("th", null, "Sent"), h("th", null, "Status"), h("th", null, "Checks"), h("th", null, h("span", { class: "sr-only" }, "Actions")))),
                  h("tbody", null, a.versions.map((v) => versionRow(b, a, v, () => void draw()))),
                ),
              ),
            );
          }),
          publishHelp(),
        );
      };
      await draw();
    }),
  );
}

// ---- moderators: review queue ------------------------------------------------------------------------------------

export function diffTable(lines: DiffLine[], first: boolean): HTMLElement {
  if (!lines.length) return h("p", { class: "dim" }, "Nothing changed in asset.json but the version.");
  const show = (v?: string) => (v === undefined ? "" : v.length > 80 ? `${v.slice(0, 77)}…` : v);
  return h(
    "div",
    { class: "table-wrap" },
    h(
      "table",
      { class: "diff" },
      h("thead", null, h("tr", null, h("th", null, "Field"), first ? null : h("th", null, "Before"), h("th", null, first ? "Value" : "After"))),
      h(
        "tbody",
        null,
        lines.map((l) =>
          h(
            "tr",
            { class: `diff-${l.kind}${l.flag ? " flagged" : ""}` },
            h("td", null, h("code", null, l.path), l.flag ? h("span", { class: "badge warn" }, l.flag) : null),
            first ? null : h("td", null, h("code", null, show(l.before))),
            h("td", null, h("code", null, show(l.after))),
          ),
        ),
      ),
    ),
  );
}

/** The render job on a review card, with Retry for a job that failed or keeps failing (POST …/render/retry). */
function renderJobBox(b: StoreBackend, item: ReviewItem, job: RenderJob | null | undefined, done: (msg: string) => void): HTMLElement | null {
  if (!job || job.state === "done") return null;
  const t = renderJobText(job);
  const retry =
    job.state === "failed" || (job.state === "queued" && job.attempts > 0)
      ? h(
          "button",
          {
            type: "button",
            class: "btn small ghost",
            onclick: async (e: Event) => {
              const btn = e.currentTarget as HTMLButtonElement;
              btn.disabled = true;
              try {
                await b.retryRender(item.id, item.version);
                done(`${item.id}@${item.version} queued to render again`);
              } catch (err) {
                toast(err instanceof Error ? err.message : String(err));
                btn.disabled = false;
              }
            },
          },
          "Render again",
        )
      : null;
  return h("div", { class: `render-job ${t.cls}` }, h("p", { class: "small" }, h("strong", null, "Render queue: "), t.text, job.worker && job.state === "running" ? ` (${job.worker})` : ""), retry);
}

/** Counts from GET /v1/review/jobs, in one line. */
export function renderQueueLine(q: RenderQueue): string {
  const c = q.counts;
  const bits = [`${c.queued} waiting`, `${c.running} rendering`];
  if (c.failed) bits.push(`${c.failed} couldn't run`);
  return `Render reviewer: ${bits.join(", ")}.`;
}

function reviewCard(b: StoreBackend, item: ReviewItem, done: (msg: string) => void, job?: RenderJob | null): HTMLElement {
  const first = !item.previous;
  const all = manifestDiff(item.previous?.manifest ?? null, item.manifest);
  // a first version: show what matters, not every file hash and param bound
  const lines = first ? all.filter((l) => /^(title|kind|price|licence|author|capabilities|conventions|engines|dependencies|cosmetic|wear|physics\.body|budget|behaviours)/.test(l.path)) : all;
  const flagged = all.filter((l) => l.flag).length;

  const stage = h("div", { class: "stage review-stage", role: "img", "aria-label": `Baked model of ${item.title}` }, h("p", { class: "stage-note" }, ""));
  const stageNote = stage.querySelector(".stage-note") as HTMLElement;
  const shot = h("div", { class: "review-shot" }, item.previewUrl ? h("img", { src: item.previewUrl, alt: `Preview of ${item.title}`, loading: "lazy" }) : h("p", { class: "dim small" }, "Previews go public once a version is listed. Turn the baked model to look at it."));
  const load3d = h(
    "button",
    {
      type: "button",
      class: "btn small ghost",
      onclick: async () => {
        load3d.remove();
        shot.replaceWith(stage);
        stageNote.textContent = "Loading the baked model…";
        const bake = item.manifest.bake?.default;
        try {
          if (!bake) throw new Error("this version has no baked model");
          // unlisted files need the moderator's session: one signed download link for the bake
          const bakedUrl = await b.fileUrl(item.id, item.version, bake);
          startAssetPreview(stage, { base: item.base, manifest: item.manifest, mode: "baked", bakedUrl, onNote: (t) => (stageNote.textContent = t) }).show({});
        } catch (e) {
          stageNote.textContent = `Couldn't load the model: ${e instanceof Error ? e.message : String(e)}`;
        }
      },
    },
    "Turn it in 3D",
  );

  const note = h("textarea", { rows: 3, maxlength: 1000, placeholder: "A note for the creator (required to reject)", "aria-label": `Note for ${item.id}@${item.version}` });
  const act = async (decision: "approve" | "reject", btn: HTMLButtonElement) => {
    if (decision === "reject" && !note.value.trim()) {
      toast("Say what needs fixing, so the creator can send it again.");
      note.focus();
      return;
    }
    if (decision === "approve" && skip?.checked && !note.value.trim()) {
      toast("Say why you're listing it without a render report.");
      note.focus();
      return;
    }
    btn.disabled = true;
    try {
      await b.review(item.id, item.version, decision, note.value.trim(), { skipRender: decision === "approve" && !!skip?.checked });
      done(decision === "approve" ? `${item.id}@${item.version} listed` : `${item.id}@${item.version} sent back`);
    } catch (e) {
      const why: Record<string, string> = {
        render_required: "There's no render report yet. Run the render reviewer, or tick “List without a render report” and say why in the note.",
        render_failed: `The render check failed, so it can't be listed. ${e instanceof Error ? e.message : ""}`,
        checks_failed: "The automated checks didn't pass, so it can't be listed.",
        not_pending: "Someone already reviewed this version.",
        no_signing_key: "The store has no signing key, so nothing can be listed right now.",
      };
      toast((e instanceof ApiError && why[e.code]) || (e instanceof Error ? e.message : String(e)));
      btn.disabled = false;
    }
  };
  const entry = item.manifest.engines?.three?.entry;
  // the render reviewer (headless Chrome) hasn't reported: approving needs it, or a note saying why not
  const skip = item.renderMissing ? h("input", { type: "checkbox" }) : null;
  const codeBox = h("div", { class: "code-box" });
  const readCode = entry
    ? h(
        "button",
        {
          type: "button",
          class: "btn small ghost",
          onclick: async (e: Event) => {
            const btn = e.currentTarget as HTMLButtonElement;
            btn.disabled = true;
            try {
              const res = await fetch(await b.fileUrl(item.id, item.version, entry));
              if (!res.ok) throw new Error(`the store answered ${res.status}`);
              const text = await res.text();
              clear(codeBox, h("p", { class: "dim small" }, `${entry}, ${text.length.toLocaleString("en-US")} characters`), h("pre", { class: "code code-read" }, h("code", null, text.slice(0, 200_000))));
              btn.remove();
            } catch (err) {
              clear(codeBox, h("p", { class: "error" }, err instanceof Error ? err.message : String(err)));
              btn.disabled = false;
            }
          },
        },
        `Read ${entry}`,
      )
    : null;
  return h(
    "section",
    { class: "panel review-item" },
    h(
      "header",
      { class: "review-item-head" },
      h("h2", null, item.title, " ", h("code", { class: "small" }, `${item.id}@${item.version}`)),
      h(
        "p",
        { class: "dim small" },
        `by ${item.creator.name ?? item.creator.handle} · sent ${day(item.submittedAt)} · `,
        first ? h("span", { class: "badge" }, "First version") : `update from ${item.previous!.version}`,
        ` · ${offerText(item.manifest.price ?? 0)}`,
      ),
    ),
    h(
      "div",
      { class: "review-grid" },
      h("div", { class: "review-media" }, shot, load3d, item.stats ? statsList(item.stats, item.manifest.budget) : null, h("p", { class: "dim small" }, "The 3D view shows the baked model only; the asset's own code isn't run here.")),
      h(
        "div",
        { class: "review-facts" },
        h("h3", null, "Automated checks"),
        checksList(item.checks),
        renderJobBox(b, item, job ?? item.renderJob, done),
        item.hasCode
          ? h(
              "p",
              { class: "small" },
              h("strong", null, "Has code. "),
              "Read it before listing.",
              item.manifest.capabilities?.length ? ` It asks for: ${item.manifest.capabilities.join(", ")}.` : " It asks for no capabilities.",
            )
          : h("p", { class: "dim small" }, "Model only: no code."),
      ),
    ),
    h("h3", null, first ? "Manifest" : `What changed since ${item.previous!.version}`, flagged ? h("span", { class: "badge warn" }, `${flagged} to look at`) : null),
    diffTable(lines, first),
    readCode || codeBox.childNodes.length ? h("div", { class: "code-read-wrap" }, readCode, codeBox) : null,
    h(
      "div",
      { class: "review-actions" },
      note,
      skip ? h("label", { class: "check skip-render" }, skip, "List without a render report (say why in the note)") : null,
      h(
        "div",
        { class: "actions" },
        h("button", { type: "button", class: "btn small", onclick: (e: Event) => void act("approve", e.currentTarget as HTMLButtonElement) }, "Approve and list"),
        h("button", { type: "button", class: "btn small danger", onclick: (e: Event) => void act("reject", e.currentTarget as HTMLButtonElement) }, "Reject"),
      ),
    ),
  );
}

export function reviewQueueView(): HTMLElement {
  return h(
    "div",
    null,
    h("h1", null, "Asset review"),
    h("p", { class: "lead" }, "New versions wait here until a moderator lists them. Check the automated results, look at the model, read any code, and compare with the previous version."),
    section(null, async (body) => {
      const b = await store();
      let first = true;
      const draw = async (msg?: string) => {
        let items: ReviewItem[];
        try {
          const probed = first ? takeProbedQueue() : null;
          first = false;
          items = await (probed ?? b.queue());
        } catch (e) {
          if (e instanceof ApiError && e.status === 403) {
            clear(body, h("p", { class: "note" }, "Only moderators see the review queue."));
            return;
          }
          clear(body, errorBox(e));
          return;
        }
        if (msg) toast(msg);
        // the render reviewer's queue: counts and each job (with max attempts); the cards fall back to render_job
        const jobs = await b.renderQueue().catch(() => null);
        clear(
          body,
          sampleNote(b),
          h("p", { class: "dim count" }, `${items.length} waiting`, jobs ? h("span", { class: "render-counts" }, ` · ${renderQueueLine(jobs)}`) : null),
          items.length === 0 ? h("p", { class: "dim" }, "The queue is empty.") : items.map((i) => reviewCard(b, i, (m) => void draw(m), jobs?.jobs.get(`${i.id}@${i.version}`))),
        );
      };
      await draw();
    }),
  );
}
